import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import { Button } from '@shared/components/ui/button';
import { faEllipsisVertical } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { DollarSign, List, Plus } from 'lucide-react';
import { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { usePermissions } from '@shared/hooks/usePermissions';

interface PayoutsHeaderActionsProps {
  onViewItems: () => void;
  onCreatePayout: () => void;
  onCreateAdvances: () => void;
}

export const PayoutsHeaderActions: FC<PayoutsHeaderActionsProps> = ({
  onViewItems,
  onCreatePayout,
  onCreateAdvances,
}) => {
  const { t } = useTranslation('codPayouts');
  const { canAccess } = usePermissions();

  const canViewItems = canAccess(['tenant_3pl'], ['cod-payments.payments.view']);
  const canCreate = canAccess(['tenant_3pl'], ['cod-payments.payouts.create']);

  const actions = useMemo(() => {
    const allActions = [];

    if (canViewItems) {
      allActions.push({
        id: 'view-items',
        label: t('items.view_button', 'Ver Items'),
        icon: List,
        onClick: onViewItems,
      });
    }

    if (canCreate) {
      allActions.push({
        id: 'create-payout',
        label: t('payout.create_button', 'Crear Liquidación'),
        icon: DollarSign,
        onClick: onCreatePayout,
      });
      allActions.push({
        id: 'create-advances',
        label: t('advance.create_button', 'Crear Anticipos'),
        icon: Plus,
        onClick: onCreateAdvances,
      });
    }

    return allActions;
  }, [canViewItems, canCreate, t, onViewItems, onCreatePayout, onCreateAdvances]);

  if (actions.length === 0) return null;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="icon" className="bg-card-bg">
          <FontAwesomeIcon icon={faEllipsisVertical} className="!w-3 !h-3" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-52 border-card-border">
        {actions.map((action) => {
          const IconComponent = action.icon;
          return (
            <DropdownMenuItem key={action.id} onClick={action.onClick} className="whitespace-nowrap">
              <IconComponent className="mr-2 h-4 w-4" />
              {action.label}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
